import { useMemo } from 'react';
import { type QueryClient, useQuery } from '@tanstack/react-query';
import { supabase } from '@/lib/supabaseClient';
import { logAndThrow } from '@/utils/logging';
import type { CupRound } from '@/lib/competition/cupRounds';

export const CUP_ROUNDS_QUERY_KEY = ['cup_rounds'] as const;

async function fetchCupRounds(): Promise<CupRound[]> {
  const { data, error } = await supabase
    .from('cup_rounds')
    .select('code, sort_order, name, name_ar, short_name, short_name_ar')
    .order('sort_order', { ascending: true });
  if (error) return logAndThrow('fetch cup_rounds', error);
  return (data ?? []).map((r) => ({
    code: r.code,
    sortOrder: r.sort_order,
    name: r.name,
    nameAr: r.name_ar,
    shortName: r.short_name,
    shortNameAr: r.short_name_ar,
  }));
}

const EMPTY: CupRound[] = [];

const cupRoundsQuery = {
  queryKey: CUP_ROUNDS_QUERY_KEY,
  queryFn: fetchCupRounds,
  staleTime: Infinity,
  gcTime: Infinity,
} as const;

/**
 * Tours de coupe pour un export lancé d'un clic : le cache de useCupRounds, attendu s'il
 * est encore en lecture ; Map vide si la lecture échoue (l'export affiche alors le code du
 * tour, jamais « Tour non précisé » pour un match qui a un tour).
 */
export async function loadCupRoundsMap(queryClient: QueryClient): Promise<ReadonlyMap<string, CupRound>> {
  try {
    const rows = await queryClient.ensureQueryData(cupRoundsQuery);
    return new Map(rows.map((r) => [r.code, r] as const));
  } catch {
    return new Map();
  }
}

/** Tours de coupe (cup_rounds, écrits seulement par migration) : lus une fois par session. */
export function useCupRounds(): {
  rounds: ReadonlyMap<string, CupRound>;
  ordered: CupRound[];
  isLoading: boolean;
  /** Lecture de cup_rounds en échec : la liste reste vide, à signaler plutôt qu'à taire. */
  isError: boolean;
} {
  const { data, isLoading, isError } = useQuery(cupRoundsQuery);
  const ordered = data ?? EMPTY;
  const rounds = useMemo(() => new Map(ordered.map((r) => [r.code, r] as const)), [ordered]);
  return { rounds, ordered, isLoading, isError };
}
